/**
 * @copyright
 * Copyright (c) 2024 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import { Action, ThunkDispatch } from "@reduxjs/toolkit";
import qs from "qs";
import { FC, ReactNode, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { getRouteConfig } from "../../../../config/route.config";
import { selectAuthAccount, selectAuthInitialized } from "~/features/auth/selectors";
import { initAuthAsync } from "~/features/auth/thunks";
import { ComponentBaseProps } from "~/types";

interface Props extends ComponentBaseProps {
  fallback?: ReactNode;
}

const AuthWrapper: FC<Props> = ({ fallback, children }) => {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, Action>>();
  const location = useLocation();
  const navigate = useNavigate();

  const account = useSelector(selectAuthAccount);
  const authInitialized = useSelector(selectAuthInitialized);

  const routeConfig = getRouteConfig(location.pathname);

  const isPublicRoute = !routeConfig.auth;

  const token = routeConfig.allowedWithToken;
  const isAllowedWithToken = !!token;
  const tokenValue = qs.parse(location.search, { ignoreQueryPrefix: true })[(token as string) ?? ""];

  // Auth init
  useEffect(() => {
    void dispatch(initAuthAsync());
  }, [dispatch]);

  // Protected routes
  useEffect(() => {
    if (!isPublicRoute && authInitialized && !account && (!isAllowedWithToken || (isAllowedWithToken && !tokenValue))) {
      const returnUrl = `${location.pathname}${location.hash ?? ""}${location.search ?? ""}`;

      if (location.pathname.match(/\/app\/logout/)) {
        void navigate("/app/login");
      } else {
        const query = qs.stringify({ returnUrl });

        void navigate(`/app/login?${query}`, { replace: true });
      }
    }
    // eslint-disable-next-line
  }, [isPublicRoute, authInitialized, account, navigate, location]);

  if (!isPublicRoute && !authInitialized) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default AuthWrapper;
